
// ! rename the function to align with the widget name
export const tabs = () => {
    const el = document.querySelector('.curious-tabs');
    if (!el) return;

    // ------------------------------------------------------------
    class CustomTabs {
        constructor(element) {
            this.wrapper = element;
            this.tabTitles = this.wrapper.querySelectorAll('.custom-tab-title');
            this.tabContents = this.wrapper.querySelectorAll('.custom-tab-content');
            this.init();
        }

        init() {
            this.bindEvents();
            this.setInitialState();
        }

        bindEvents() {
            // Click event for tab titles
            this.tabTitles.forEach(tab => {
                tab.addEventListener('click', (e) => {
                    this.switchTab(e.currentTarget);
                });

                // Keyboard navigation
                tab.addEventListener('keydown', (e) => {
                    this.handleKeydown(e);
                });
            });
        }

        setInitialState() {
            // Ensure first tab is active by default
            if (this.tabTitles.length > 0) {
                const firstTab = this.tabTitles[0];
                const firstContent = this.tabContents[0];

                firstTab.classList.add('active');
                firstTab.setAttribute('tabindex', '0');
                firstTab.setAttribute('aria-selected', 'true');

                if (firstContent) {
                    firstContent.classList.add('active');
                    firstContent.setAttribute('aria-hidden', 'false');
                }

                // Hide other tabs
                for (let i = 1; i < this.tabTitles.length; i++) {
                    this.tabTitles[i].classList.remove('active');
                    this.tabTitles[i].setAttribute('tabindex', '-1');
                    this.tabTitles[i].setAttribute('aria-selected', 'false');
                }

                for (let i = 1; i < this.tabContents.length; i++) {
                    this.tabContents[i].classList.remove('active');
                    this.tabContents[i].setAttribute('aria-hidden', 'true');
                }
            }
        }

        switchTab(clickedTab) {
            const targetTab = clickedTab.getAttribute('data-tab');
            
            if (clickedTab.classList.contains('active')) {
                return; // Already active
            }

            // Remove active class from all tabs and contents
            this.tabTitles.forEach(tab => {
                tab.classList.remove('active');
                tab.setAttribute('tabindex', '-1');
                tab.setAttribute('aria-selected', 'false');
            });

            this.tabContents.forEach(content => {
                content.classList.remove('active');
                content.setAttribute('aria-hidden', 'true');
            });

            // Add active class to clicked tab and corresponding content
            clickedTab.classList.add('active');
            clickedTab.setAttribute('tabindex', '0');
            clickedTab.setAttribute('aria-selected', 'true');

            const targetContent = this.wrapper.querySelector(`.custom-tab-content[data-tab="${targetTab}"]`);
            if (targetContent) {
                targetContent.classList.add('active');
                targetContent.setAttribute('aria-hidden', 'false');
            }

            // Focus the clicked tab for accessibility
            clickedTab.focus();

            // Trigger custom event
            const customEvent = new CustomEvent('customTabChanged', {
                detail: {
                    tabNumber: targetTab,
                    tabElement: clickedTab,
                    contentElement: targetContent
                },
                bubbles: true
            });
            this.wrapper.dispatchEvent(customEvent);
        }

        handleKeydown(e) {
            const target = e.target;
            let newTarget = null;
            const tabsArray = Array.from(this.tabTitles);
            const currentIndex = tabsArray.indexOf(target);

            switch (e.which || e.keyCode) {
                case 37: // Left arrow
                case 38: // Up arrow
                    e.preventDefault();
                    newTarget = tabsArray[currentIndex - 1] || tabsArray[tabsArray.length - 1];
                    break;

                case 39: // Right arrow
                case 40: // Down arrow
                    e.preventDefault();
                    newTarget = tabsArray[currentIndex + 1] || tabsArray[0];
                    break;

                case 36: // Home
                    e.preventDefault();
                    newTarget = tabsArray[0];
                    break;

                case 35: // End
                    e.preventDefault();
                    newTarget = tabsArray[tabsArray.length - 1];
                    break;

                case 13: // Enter
                case 32: // Space
                    e.preventDefault();
                    this.switchTab(target);
                    return;

                default:
                    return;
            }

            if (newTarget) {
                this.switchTab(newTarget);
            }
        }

        // Public method to switch to specific tab
        goToTab(tabIndex) {
            const targetTab = this.tabTitles[tabIndex];
            if (targetTab) {
                this.switchTab(targetTab);
            }
        }

        // Public method to get current active tab
        getCurrentTab() {
            const activeTab = this.wrapper.querySelector('.custom-tab-title.active');
            return activeTab ? activeTab.getAttribute('data-tab') : null;
        }

        // Public method to destroy the instance
        destroy() {
            this.tabTitles.forEach(tab => {
                tab.removeEventListener('click', this.switchTab);
                tab.removeEventListener('keydown', this.handleKeydown);
            });
        }
    }

    // Utility functions
    function getCustomTabsInstance(element) {
        if (typeof element === 'string') {
            element = document.querySelector(element);
        }
        const wrapper = element.closest('.custom-tabs-wrapper');
        return wrapper ? wrapper.customTabsInstance : null;
    }

    function animateTabContent(content) {
        if (!content) return;
        
        content.style.opacity = '0';
        content.style.transform = 'translateY(20px)';
        
        requestAnimationFrame(() => {
            content.style.transition = 'all 0.3s ease';
            content.style.opacity = '1';
            content.style.transform = 'translateY(0)';
        });
    }

    // Initialize tabs function
    function initCustomTabs() {
        const tabsWrappers = document.querySelectorAll('.custom-tabs-wrapper');
        
        tabsWrappers.forEach(wrapper => {
            if (!wrapper.customTabsInstance) {
                wrapper.customTabsInstance = new CustomTabs(wrapper);
            }
        });
    }

    // DOM ready function
    function domReady(fn) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', fn);
        } else {
            fn();
        }
    }

    // Initialize on DOM ready
    domReady(initCustomTabs);

    // Re-initialize when Elementor preview is refreshed
    window.addEventListener('elementor/frontend/init', function() {
        if (typeof elementorFrontend !== 'undefined') {
            elementorFrontend.hooks.addAction('frontend/element_ready/custom-tabs.default', function(scope) {
                const tabsWrapper = scope.querySelector('.custom-tabs-wrapper');
                if (tabsWrapper && !tabsWrapper.customTabsInstance) {
                    tabsWrapper.customTabsInstance = new CustomTabs(tabsWrapper);
                }
            });
        }
    });

    // Handle custom tab changed events
    document.addEventListener('customTabChanged', function(e) {
        const detail = e.detail;
        const content = detail.contentElement;
        
        if (content) {
            animateTabContent(content);
        }
        
        // You can add additional custom functionality here
        console.log('Tab changed to:', detail.tabNumber);
    });

    // Public API for external use
    window.CustomTabsAPI = {
        switchTab: function(selector, tabIndex) {
            const instance = getCustomTabsInstance(selector);
            if (instance) {
                instance.goToTab(tabIndex);
            }
        },
        getCurrentTab: function(selector) {
            const instance = getCustomTabsInstance(selector);
            return instance ? instance.getCurrentTab() : null;
        },
        getInstance: getCustomTabsInstance,
        init: initCustomTabs
    };

    // Expose the class for advanced usage
    window.CustomTabs = CustomTabs;

    // Handle AJAX content loading
    let ajaxObserver;
    
    function handleAjaxContent() {
        // Use MutationObserver to watch for new content
        if (!ajaxObserver) {
            ajaxObserver = new MutationObserver(function(mutations) {
                let shouldInit = false;
                
                mutations.forEach(function(mutation) {
                    mutation.addedNodes.forEach(function(node) {
                        if (node.nodeType === 1) { // Element node
                            if (node.classList.contains('custom-tabs-wrapper') || 
                                node.querySelector('.custom-tabs-wrapper')) {
                                shouldInit = true;
                            }
                        }
                    });
                });
                
                if (shouldInit) {
                    setTimeout(initCustomTabs, 100);
                }
            });
            
            ajaxObserver.observe(document.body, {
                childList: true,
                subtree: true
            });
        }
    }

    // Start observing for AJAX content
    domReady(handleAjaxContent);

    // Fallback for older browsers or specific AJAX implementations
    const originalXHR = window.XMLHttpRequest;
    if (originalXHR) {
        window.XMLHttpRequest = function() {
            const xhr = new originalXHR();
            const originalOpen = xhr.open;
            
            xhr.open = function() {
                this.addEventListener('load', function() {
                    setTimeout(initCustomTabs, 100);
                });
                originalOpen.apply(this, arguments);
            };
            
            return xhr;
        };
    }

    // Handle page visibility changes (for performance)
    document.addEventListener('visibilitychange', function() {
        if (!document.hidden) {
            // Re-initialize when page becomes visible
            setTimeout(initCustomTabs, 100);
        }
    });

    // ------------------------------------------------------------
    // ! Remember to call this function in the main script file: /site/src/js/site.js

}

